package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.browser.account.GraphQL
import com.steamstreet.vegasful.graphql.api.client.Venue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlin.math.roundToLong
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement

/*
 * ---------------------------------------------------------------------------
 *  MapLibre GL JS bindings
 *
 *  MapLibre GL JS is the open-source fork of Mapbox GL JS and exposes the same
 *  global API surface we use here (Map / LngLatBounds, addSource / addLayer,
 *  flyTo, fitBounds). To switch to Mapbox proper:
 *    1. In venuemap.kt, point the <link>/<script> at the mapbox-gl CDN.
 *    2. Rename the global below from `maplibregl` to `mapboxgl`.
 *    3. Set `mapboxgl.accessToken = <token>` before constructing the Map, and
 *       use a Mapbox style URL instead of the inline raster style.
 * ---------------------------------------------------------------------------
 */
@Suppress("ClassName")
private external object maplibregl {
    class Map(options: dynamic) {
        fun flyTo(options: dynamic)
        fun jumpTo(options: dynamic)
        fun fitBounds(bounds: dynamic, options: dynamic = definedExternally)
        fun on(type: String, listener: (dynamic) -> Unit)
        fun on(type: String, layer: String, listener: (dynamic) -> Unit)
        fun addSource(id: String, source: dynamic)
        fun getSource(id: String): dynamic
        fun addLayer(layer: dynamic)
        fun getCanvas(): HTMLElement
    }

    class LngLatBounds {
        fun extend(lngLat: dynamic): LngLatBounds
    }
}

/**
 * Ids shared between the Kotlin code and the inline `js(...)` layer definitions
 * below. The `js(...)` argument must be a compile-time constant, so the same
 * strings are repeated there literally — keep the two in sync.
 */
private const val SOURCE_ID = "venue-source"
private const val POINTS_LAYER = "venue-points"
private const val LABELS_LAYER = "venue-labels"

/** Las Vegas Strip, used as the initial camera target before venues load. */
private val LAS_VEGAS_CENTER = arrayOf(-115.1728, 36.1147)

/** A lightweight client-side view of a venue and the data we need to draw it. */
private class VenueNode(
    val id: String,
    val name: String,
    val lat: Double?,
    val lng: Double?,
    val hasChildren: Boolean,
    val children: List<VenueNode>
)

/**
 * One plotted location. Venues that share an exact coordinate — a resort's
 * theaters and clubs are frequently geocoded to the resort itself — collapse
 * into a single point carrying all of them, so that no venue is silently
 * hidden underneath another.
 */
private class MapPoint(
    val lat: Double,
    val lng: Double,
    val venues: List<VenueNode>
) {
    val label: String get() = if (venues.size == 1) venues.first().name else "${venues.size} venues"

    /** True when clicking this point leads somewhere: a list, or a deeper level. */
    val drills: Boolean get() = venues.size > 1 || venues.first().hasChildren
}

private val scope = CoroutineScope(Dispatchers.Default)

private var map: maplibregl.Map? = null

/** The points currently drawn, indexed by the `idx` property carried on each feature. */
private var points: List<MapPoint> = emptyList()

/** The drill-down trail: each entry is (venueId-or-null-for-root, displayName). */
private val trail = mutableListOf<Pair<String?, String>>()

/**
 * Entry point registered in the module map. Waits for the MapLibre script to load,
 * initializes the map, and loads the starting level.
 */
fun venueMap(element: Element) {
    val start = element.getAttribute("data-start-venue")
    val basemap = element.getAttribute("data-basemap")
    val archive = element.getAttribute("data-basemap-archive")
    whenMapLibreReady {
        initMap(basemap, archive) {
            scope.launch {
                loadLevel(start, name = null, newLevel = true, fly = false)
            }
        }
    }
}

/**
 * The vector basemap needs three globals: `maplibregl`, plus `pmtiles` and
 * `basemaps` from the script tags in venuemap.kt. Waiting on all three keeps the
 * page from initializing against a half-loaded set.
 */
private fun whenMapLibreReady(block: () -> Unit) {
    if (mapLibrariesLoaded()) {
        block()
    } else {
        window.setTimeout({ whenMapLibreReady(block) }, 50)
    }
}

private fun mapLibrariesLoaded(): Boolean =
    js("typeof maplibregl !== 'undefined' && typeof pmtiles !== 'undefined' && typeof basemaps !== 'undefined'") as Boolean

/**
 * Build the map and register the venue source and layers. [onReady] fires once the
 * style has loaded, which is the earliest point at which sources may be added.
 *
 * [basemapBase] and [archive] come from the server; when either is absent (local
 * development without `BasemapBaseUrl` configured) the map falls back to raster
 * tiles so the page still works.
 */
private fun initMap(basemapBase: String?, archive: String?, onReady: () -> Unit) {
    if (map != null) {
        onReady()
        return
    }
    registerPmtilesProtocol()

    val options = js("({})")
    options.container = "venue-map-canvas"
    options.style = if (basemapBase != null && archive != null) {
        vectorStyle(basemapBase, archive)
    } else {
        rasterFallbackStyle()
    }
    options.center = LAS_VEGAS_CENTER
    options.zoom = 11
    val created = maplibregl.Map(options)
    map = created
    created.on("load") {
        addVenueLayers(created)
        registerInteractions(created)
        onReady()
    }
}

/**
 * Teach MapLibre the `pmtiles://` scheme. The archive is a single file on
 * CloudFront that the client reads with HTTP range requests; there is no tile
 * server. Registering twice throws, so this is guarded.
 */
private fun registerPmtilesProtocol() {
    if (js("typeof maplibregl.getProtocol === 'function' && maplibregl.getProtocol('pmtiles')") as? Boolean == true) return
    js("(function(){ var p = new pmtiles.Protocol(); maplibregl.addProtocol('pmtiles', p.tile); })()")
}

/**
 * Base layers we drop from the generated Protomaps style. The venue overlay is the
 * subject of this map; the basemap only needs to say where you are.
 *
 * `pois` and `address_label` are the bulk of the visual noise — every restaurant,
 * shop and house number in the valley, none of which is a Vegasful venue and all
 * of which competes with the venue labels for the same pixels. `roads_labels_minor`
 * goes for the same reason at the zoom levels the drill-down lands on. The ocean
 * and island labels are simply irrelevant 300 miles inland.
 *
 * Major road labels are deliberately kept: on the Strip, "Las Vegas Blvd" and
 * "Flamingo Rd" are how people orient.
 */
private val SUPPRESSED_BASE_LAYERS = setOf(
    "pois",
    "address_label",
    "roads_labels_minor",
    "earth_label_islands",
    "water_label_ocean"
)

/**
 * The self-hosted Protomaps basemap. Layers are generated by the `basemaps` package
 * rather than fetched as an opaque style URL, which is what makes the filtering
 * above possible at all — with the raster tiles this replaced, every label was
 * baked into the image and nothing could be turned off.
 *
 * Glyphs and sprites are served from the same prefix as the archive, so the whole
 * basemap has a single origin and no third-party runtime dependency.
 */
private fun vectorStyle(base: String, archive: String): dynamic {
    val style = js("({})")
    style.version = 8
    style.glyphs = "${base}fonts/{fontstack}/{range}.pbf"
    style.sprite = "${base}sprites/grayscale"

    val source = js("({})")
    source.type = "vector"
    source.url = "pmtiles://$base$archive"
    // ODbL requires attribution for OpenStreetMap-derived data.
    source.attribution = "© OpenStreetMap contributors"

    val sources = js("({})")
    sources.protomaps = source
    style.sources = sources

    val generated = js("basemaps.layers('protomaps', basemaps.namedFlavor('grayscale'), {lang: 'en'})")
    val kept: dynamic = js("([])")
    val count = (generated.length as Number).toInt()
    for (index in 0 until count) {
        val layer = generated[index]
        if ((layer.id as? String) !in SUPPRESSED_BASE_LAYERS) {
            kept.push(layer)
        }
    }
    style.layers = kept
    return style
}

/**
 * Development-only fallback for when no basemap is configured, so that
 * `:server:local:run` without `BasemapBaseUrl` still renders something.
 *
 * Both endpoints are public demo/community services and must not be relied on in
 * production — the OSM tile usage policy excludes it outright, and the glyph
 * endpoint is a demo. That is precisely why the self-hosted basemap exists; this
 * path should never be reached on a deployed stage, where the web stack always
 * sets `BasemapBaseUrl`.
 */
private fun rasterFallbackStyle(): dynamic = js(
    "({version:8," +
        "glyphs:'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf'," +
        "sources:{osm:{type:'raster',tiles:['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],tileSize:256,attribution:'© OpenStreetMap contributors'}}," +
        "layers:[{id:'osm',type:'raster',source:'osm'}]})"
)

/**
 * The venue overlay: a circle per plotted point, a count badge on points holding
 * more than one venue, and a label layer.
 *
 * Labels are a symbol layer rather than HTML markers specifically for collision
 * handling — MapLibre drops labels that would overlap and lets the rest shuffle
 * around their anchor. Levels inside a single resort put venues within a few
 * hundred feet of each other, where hand-placed labels would pile up illegibly.
 * Circles are exempt: they always draw, so a venue never disappears entirely
 * just because its label lost a collision.
 */
private fun addVenueLayers(target: maplibregl.Map) {
    target.addSource(SOURCE_ID, js("({type:'geojson',data:{type:'FeatureCollection',features:[]}})"))

    target.addLayer(
        js(
            "({id:'venue-points',type:'circle',source:'venue-source'," +
                "paint:{'circle-radius':['case',['>',['get','count'],1],12,7]," +
                "'circle-color':['case',['==',['get','drills'],1],'#ca0045','#5c003f']," +
                "'circle-stroke-width':2,'circle-stroke-color':'#ffffff'}})"
        )
    )

    // Count badge, drawn inside the circle for grouped points. Overlap is forced
    // on: the badge belongs to its circle and must never be dropped separately.
    target.addLayer(
        js(
            "({id:'venue-counts',type:'symbol',source:'venue-source'," +
                "filter:['>',['get','count'],1]," +
                "layout:{'text-field':['to-string',['get','count']],'text-font':['Noto Sans Regular']," +
                "'text-size':12,'text-allow-overlap':true,'text-ignore-placement':true}," +
                "paint:{'text-color':'#ffffff'}})"
        )
    )

    target.addLayer(
        js(
            "({id:'venue-labels',type:'symbol',source:'venue-source'," +
                "layout:{'text-field':['get','label'],'text-font':['Noto Sans Regular']," +
                "'text-size':13,'text-variable-anchor':['left','right','top','bottom']," +
                "'text-radial-offset':1.1,'text-justify':'auto','text-padding':4,'text-max-width':12," +
                "'symbol-sort-key':['-',0,['get','count']]}," +
                "paint:{'text-color':'#1a1a1a','text-halo-color':'#ffffff','text-halo-width':2}})"
        )
    )
}

/** Clicks and hover affordances for both the circles and their labels. */
private fun registerInteractions(target: maplibregl.Map) {
    listOf(POINTS_LAYER, LABELS_LAYER).forEach { layer ->
        target.on("click", layer) { event ->
            val feature = event.features?.get(0) ?: return@on
            val index = (feature.properties.idx as Number).toInt()
            points.getOrNull(index)?.let { onPointSelected(it) }
        }
        target.on("mouseenter", layer) {
            target.getCanvas().style.cursor = "pointer"
        }
        target.on("mouseleave", layer) {
            target.getCanvas().style.cursor = ""
        }
    }
}

/**
 * Load a venue level and render its children.
 *
 * @param venueId the venue whose children to show; null means the root venue.
 * @param name display name for the breadcrumb (falls back to the loaded venue name).
 * @param newLevel true when drilling into a new level (pushes onto the trail).
 * @param fly whether to animate the camera to the new bounds.
 */
private suspend fun loadLevel(venueId: String?, name: String?, newLevel: Boolean, fly: Boolean) {
    setStatus("Loading venues…")
    hideList()
    val node = try {
        fetchLevel(venueId)
    } catch (t: Throwable) {
        setStatus("Could not load venues. Please try again.")
        return
    }

    if (newLevel) {
        trail.add(venueId to (name ?: node.name))
    }
    renderBreadcrumb()

    points = groupByLocation(node.children)
    renderPoints(fly)

    when {
        node.children.isEmpty() ->
            setStatus("No venues inside ${node.name}.")

        // Nothing is mapped: the map can say nothing useful, so the list carries
        // the whole level.
        points.isEmpty() -> {
            setStatus("No mapped locations inside ${node.name}.")
            showList("Inside ${node.name}", node.children)
        }

        // Every child sits on one coordinate — typical of venues inside a single
        // building. A map of one point communicates nothing, so lead with the list.
        points.size == 1 && node.children.size > 1 -> {
            setStatus("")
            showList("Inside ${node.name}", node.children)
        }

        else -> setStatus("")
    }
}

/**
 * Collapse children onto distinct coordinates. Rounding to six decimals (~4 inches)
 * groups records that were geocoded to the same place while keeping genuinely
 * separate venues apart.
 */
private fun groupByLocation(children: List<VenueNode>): List<MapPoint> {
    val groups = LinkedHashMap<String, MutableList<VenueNode>>()
    children.forEach { child ->
        val lat = child.lat
        val lng = child.lng
        if (lat != null && lng != null) {
            val key = "${(lat * 1_000_000).roundToLong()}:${(lng * 1_000_000).roundToLong()}"
            groups.getOrPut(key) { mutableListOf() }.add(child)
        }
    }
    return groups.values.map { venues ->
        MapPoint(venues.first().lat!!, venues.first().lng!!, venues)
    }
}

private suspend fun fetchLevel(venueId: String?): VenueNode {
    val result = GraphQL.client {
        venues {
            if (venueId == null) {
                root {
                    id
                    name
                    location {
                        latitude
                        longitude
                    }
                    children {
                        id
                        name
                        location {
                            latitude
                            longitude
                        }
                        children {
                            id
                        }
                    }
                }
            } else {
                venue(venueId) {
                    id
                    name
                    location {
                        latitude
                        longitude
                    }
                    children {
                        id
                        name
                        location {
                            latitude
                            longitude
                        }
                        children {
                            id
                        }
                    }
                }
            }
        }
    }
    val venue: Venue = if (venueId == null) result.venues.root else result.venues.venue
    return venue.toNode()
}

private fun Venue.toNode(): VenueNode = VenueNode(
    id = id,
    name = name,
    lat = location?.latitude?.toDouble(),
    lng = location?.longitude?.toDouble(),
    hasChildren = children.isNotEmpty(),
    children = children.map { child ->
        VenueNode(
            id = child.id,
            name = child.name,
            lat = child.location?.latitude?.toDouble(),
            lng = child.location?.longitude?.toDouble(),
            hasChildren = child.children.isNotEmpty(),
            children = emptyList()
        )
    }
)

/** Push the current [points] into the GeoJSON source and frame the camera on them. */
private fun renderPoints(fly: Boolean) {
    val activeMap = map ?: return

    val features: dynamic = js("([])")
    points.forEachIndexed { index, point ->
        val feature = js("({})")
        feature.type = "Feature"

        val geometry = js("({})")
        geometry.type = "Point"
        geometry.coordinates = arrayOf(point.lng, point.lat)
        feature.geometry = geometry

        val properties = js("({})")
        properties.idx = index
        properties.label = point.label
        properties.count = point.venues.size
        properties.drills = if (point.drills) 1 else 0
        feature.properties = properties

        features.push(feature)
    }

    val collection = js("({})")
    collection.type = "FeatureCollection"
    collection.features = features
    activeMap.getSource(SOURCE_ID)?.setData(collection)

    if (points.isEmpty()) return

    // fitBounds on a zero-area box is degenerate, so a lone point gets an
    // explicit camera move instead.
    if (points.size == 1) {
        val single = points.first()
        val camera = js("({})")
        camera.center = arrayOf(single.lng, single.lat)
        camera.zoom = 16
        if (fly) activeMap.flyTo(camera) else activeMap.jumpTo(camera)
        return
    }

    val bounds = maplibregl.LngLatBounds()
    points.forEach { bounds.extend(arrayOf(it.lng, it.lat)) }

    val cameraOptions = js("({})")
    cameraOptions.padding = 60
    cameraOptions.maxZoom = 16
    cameraOptions.duration = if (fly) 800 else 0
    cameraOptions.animate = fly
    activeMap.fitBounds(bounds, cameraOptions)
}

/** A point holding several venues opens the list; a lone venue acts directly. */
private fun onPointSelected(point: MapPoint) {
    if (point.venues.size > 1) {
        showList(point.label, point.venues)
    } else {
        onVenueActivated(point.venues.first())
    }
}

private fun onVenueActivated(venue: VenueNode) {
    if (venue.hasChildren) {
        hideList()
        scope.launch {
            loadLevel(venue.id, name = venue.name, newLevel = true, fly = true)
        }
    } else {
        // Leaf venue: jump to its detail page.
        window.location.href = "/venues/${venue.id}"
    }
}

/**
 * Show the side list. This carries the venues a map cannot separate: points
 * shared by several venues, levels where everything sits on one coordinate, and
 * levels with no coordinates at all.
 */
private fun showList(title: String, venues: List<VenueNode>) {
    val panel = document.getElementById("venue-map-list") as? HTMLElement ?: return
    panel.innerHTML = ""

    val header = document.createElement("div") as HTMLElement
    header.style.display = "flex"
    header.style.justifyContent = "space-between"
    header.style.alignItems = "baseline"
    header.style.marginBottom = "6px"

    val heading = document.createElement("strong") as HTMLElement
    heading.textContent = title
    header.appendChild(heading)

    val close = document.createElement("span") as HTMLElement
    close.textContent = "×"
    close.style.cursor = "pointer"
    close.style.paddingLeft = "12px"
    close.style.fontSize = "18px"
    close.style.lineHeight = "1"
    close.addEventListener("click", { hideList() })
    header.appendChild(close)

    panel.appendChild(header)

    venues.forEach { venue ->
        val row = document.createElement("div") as HTMLElement
        row.textContent = venue.name + if (venue.hasChildren) " ›" else ""
        row.style.cursor = "pointer"
        row.style.padding = "5px 0"
        row.style.color = "#ca0045"
        row.addEventListener("click", { onVenueActivated(venue) })
        panel.appendChild(row)
    }

    panel.style.display = "block"
}

private fun hideList() {
    val panel = document.getElementById("venue-map-list") as? HTMLElement ?: return
    panel.style.display = "none"
    panel.innerHTML = ""
}

private fun renderBreadcrumb() {
    val container = document.getElementById("venue-map-breadcrumb") ?: return
    container.innerHTML = ""

    trail.forEachIndexed { index, entry ->
        if (index > 0) {
            val separator = document.createElement("span")
            separator.textContent = " › "
            container.appendChild(separator)
        }

        val crumb = document.createElement("span") as HTMLElement
        crumb.textContent = entry.second

        if (index < trail.size - 1) {
            crumb.style.cursor = "pointer"
            crumb.style.textDecoration = "underline"
            crumb.style.color = "#ca0045"
            crumb.addEventListener("click", {
                navigateToTrail(index)
            })
        } else {
            crumb.style.fontWeight = "bold"
        }
        container.appendChild(crumb)
    }
}

private fun navigateToTrail(index: Int) {
    val target = trail[index]
    // Drop everything from this crumb onward; loadLevel re-adds it as a fresh level.
    while (trail.size > index) {
        trail.removeAt(trail.size - 1)
    }
    scope.launch {
        loadLevel(target.first, name = target.second, newLevel = true, fly = true)
    }
}

private fun setStatus(message: String) {
    val status = document.getElementById("venue-map-status") as? HTMLElement ?: return
    if (message.isBlank()) {
        status.style.display = "none"
    } else {
        status.style.display = "block"
        status.textContent = message
    }
}
